import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  InputSignal,
  InputSignalWithTransform,
  OnChanges,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  SimpleChanges,
  WritableSignal
} from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { chunkArray } from '../../../utils/chunk.util';
import { MONTHS, WEEK_DAYS } from '../../../constants/date.constants';
import { DatePickerOption } from '../../../interfaces/date-picker.interface';
import { DatePickerDayMonth } from '../../../enums/date-picker.enum';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';

const toDayStartTimestamp = (date: Date): number => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
};

export enum DatePickerSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

export enum DatePickerStyle {
  Primary = 'primary',
  Secondary = 'secondary'
}

export enum DatePickerLabelPosition {
  Inside = 'inside',
  Outside = 'outside'
}

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  imports: [DatePipe, NgClass, CdkConnectedOverlay, CdkOverlayOrigin],
  styleUrl: './date-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatePickerComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  protected readonly currentDate: Date = new Date();
  protected readonly MONTHS: string[] = MONTHS;
  protected readonly WEEK_DAYS: string[] = WEEK_DAYS;
  protected readonly DatePickerLabelPosition = DatePickerLabelPosition;

  minDate: InputSignal<Date> = input<Date>(new Date(0));
  maxDate: InputSignal<Date> = input<Date>(new Date(Infinity));
  placeholder: InputSignal<string> = input<string>('Select date');

  label: InputSignal<string> = input<string>('Date');
  labelPosition: InputSignal<DatePickerLabelPosition> =
    input<DatePickerLabelPosition>(DatePickerLabelPosition.Inside);

  style: InputSignal<DatePickerStyle> = input<DatePickerStyle>(
    DatePickerStyle.Secondary
  );
  size: InputSignal<DatePickerSize> = input<DatePickerSize>(
    DatePickerSize.Medium
  );

  outline: InputSignal<boolean> = input<boolean>(true);
  isWeekendDisabled: InputSignal<boolean> = input<boolean>(false);
  availableDates: InputSignalWithTransform<number[], (Date | string)[]> = input(
    [],
    {
      transform: (rawValue: (string | Date)[]): number[] => {
        return rawValue.map((date: Date | string) => {
          const dateObj = typeof date === 'string' ? new Date(date) : date;
          return toDayStartTimestamp(dateObj);
        });
      }
    }
  );

  select: OutputEmitterRef<Date> = output<Date>();

  year: WritableSignal<number> = signal<number>(this.currentDate.getFullYear());
  month: WritableSignal<number> = signal<number>(this.currentDate.getMonth());
  selectedDate: WritableSignal<Date | undefined> = signal<Date | undefined>(
    undefined
  );

  // State
  isCalendarOpen: WritableSignal<boolean> = signal<boolean>(false);
  isDisabled: WritableSignal<boolean> = signal<boolean>(false);
  options: WritableSignal<DatePickerOption[][]> = signal<DatePickerOption[][]>(
    []
  );

  ngOnInit() {
    this.updateOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['availableDates']) {
      this.updateOptions();
    }
  }

  onDateClick(option: DatePickerOption): void {
    console.log(option);
    if (option.dayMonth === DatePickerDayMonth.Current) {
      this.selectedDate.set(option.date);
      this.onChange(option.date);
      this.select.emit(option.date);
      this.closeCalendar();
    } else if (option.dayMonth === DatePickerDayMonth.Previous) {
      this.onPrevClick();
    } else if (option.dayMonth === DatePickerDayMonth.Next) {
      this.onNextClick();
    }

    this.onTouch();
  }

  onPrevClick() {
    this.month.update((month: number) => (month === 0 ? 11 : month - 1));
    this.year.update((year: number) => (this.month() === 11 ? year - 1 : year));
    this.updateOptions();
  }

  onNextClick() {
    this.month.update((month: number) => (month === 11 ? 0 : month + 1));
    this.year.update((year: number) => (this.month() === 0 ? year + 1 : year));
    this.updateOptions();
  }

  toggleOpen() {
    this.isCalendarOpen.update((isCalendarOpen: boolean) => !isCalendarOpen);
  }

  closeCalendar() {
    this.isCalendarOpen.set(false);
  }

  private updateOptions() {
    const options: DatePickerOption[][] = this.getGroupedDates(
      this.month(),
      this.year()
    );

    this.options.set(options);
  }

  private getGroupedDates(month: number, year: number): DatePickerOption[][] {
    const dates: DatePickerOption[] = this.getDates(month, year);
    return chunkArray<DatePickerOption>(dates, 7);
  }

  private getDates(month: number, year: number): DatePickerOption[] {
    const previousMonthEnd = new Date(year, month, 0);
    const currentMonthStart: Date = new Date(year, month, 1);
    const currentMonthEnd: Date = new Date(year, month + 1, 0);

    const previousMonthLastDate: number = previousMonthEnd.getDate();
    const currentMonthLastDay: number = currentMonthEnd.getDay();
    const currentMonthFirstDay: number = currentMonthStart.getDay();
    const currentMonthLastDate: number = currentMonthEnd.getDate();

    const prevMonth: number = this.month() === 0 ? 11 : month - 1;
    const prevYear: number = prevMonth === 11 ? this.year() - 1 : this.year();
    const previousMonthDays: DatePickerOption[] =
      currentMonthFirstDay === 0
        ? []
        : this.getDaysOptions(
            previousMonthLastDate + 1 - currentMonthFirstDay,
            previousMonthLastDate + 1,
            prevMonth,
            prevYear,
            DatePickerDayMonth.Previous
          );

    const currentMonthDays: DatePickerOption[] = this.getDaysOptions(
      1,
      currentMonthLastDate + 1,
      this.month(),
      this.year(),
      DatePickerDayMonth.Current
    );

    const nextMonth: number = this.month() === 11 ? 0 : month + 1;
    const nextYear: number = nextMonth === 0 ? this.year() + 1 : this.year();
    const nextMonthDays: DatePickerOption[] = this.getDaysOptions(
      1,
      7 - currentMonthLastDay,
      nextMonth,
      nextYear,
      DatePickerDayMonth.Next
    );

    return [...previousMonthDays, ...currentMonthDays, ...nextMonthDays];
  }

  private getDaysOptions(
    from: number,
    to: number,
    month: number,
    year: number,
    dayMonth: DatePickerDayMonth = DatePickerDayMonth.Current,
    availableDates: number[] = this.availableDates() || []
  ): DatePickerOption[] {
    const result: DatePickerOption[] = [];

    for (let day: number = from; day < to; day++) {
      const date: Date = new Date(year, month, day);
      const dayStartTimestamp = toDayStartTimestamp(date);

      result.push({
        date,
        dayMonth,
        isAvailable:
          availableDates.length > 0
            ? availableDates.includes(dayStartTimestamp)
            : true
      });
    }

    return result;
  }

  protected isDateDisabled(option: DatePickerOption): boolean {
    const dayTimestamp = toDayStartTimestamp(option.date);
    const minDateTimestamp = toDayStartTimestamp(this.minDate());
    const maxDateTimestamp = toDayStartTimestamp(this.maxDate());

    const isBeforeMinDate = Number.isFinite(minDateTimestamp)
      ? dayTimestamp < minDateTimestamp
      : false;
    const isAfterMaxDate = Number.isFinite(maxDateTimestamp)
      ? dayTimestamp > maxDateTimestamp
      : false;

    return isBeforeMinDate || isAfterMaxDate || !option.isAvailable;
  }

  // Value Accessor Functions
  private onChange = (value: Date | unknown) => {};
  private onTouch = () => {};

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  writeValue(outsideValue?: Date | string): void {
    if (!outsideValue) {
      return;
    }

    let dateObj: Date;

    // Handle Date objects
    if (outsideValue instanceof Date) {
      dateObj = outsideValue;
    } else if (typeof outsideValue === 'string') {
      // Convert string to Date object
      dateObj = new Date(outsideValue);
      if (isNaN(dateObj.getTime())) {
        // Invalid date string, don't set anything
        return;
      }
    } else {
      // Invalid type, don't set anything
      return;
    }

    this.year.set(dateObj.getFullYear());
    this.month.set(dateObj.getMonth());
    this.selectedDate.set(dateObj);
  }
}
